// Verbatim port of legacy
// frontend/src/pages/Category/PantryCategory/types.ts. Kept local to the
// Category feature folder (per plan) rather than re-exported via
// libs/pantry-features/src/types/index.ts — only the route surface
// (CategoryPage) is meant to be public.

export interface LinkedPosCategory {
  id: string | number
  isMapped: boolean
  name: string
}

// Hierarchy limits. Categories nest at most three levels deep (depth 0, 1,
// 2). The backend rejects parent cycles on update but has no depth limit, so
// the frontend is the only depth guard — see utils/categoryTree.ts.
export const MAX_CATEGORY_DEPTH = 3

export interface WebCategory {
  id: string
  parentId?: string | number
  // 0 = top-level, 1 = subcategory, 2 = sub-subcategory. Assigned by
  // buildWebCategoryTree() from the flat /categories payload.
  depth: number
  displayName: string
  // Leaf slug — unique within the parent only.
  slug: string
  // Path from the root (`concentrates/live-resin`), unique per store. Use it
  // for every storefront URL (`/products/category/<fullSlug>`).
  fullSlug: string
  slugLocked: boolean
  previousSlugs: string[]
  // Number of active 301 redirects for this category (rows in
  // `category_slug_history` matching this category that aren't the current
  // slug). Drives the "➜ N redirect(s)" badge in the Slug column. Sourced
  // from `redirect_count` on the list endpoint — defaults to 0 when the
  // backend omits the field.
  redirectCount: number
  visible: boolean
  headline: string
  description: string
  seoTitle: string
  seoDescription: string
  products: number
  thumbnail: string | null
  bannerImage?: string | null
  posMapping: 'mapped' | 'unmapped' | 'partial' | null
  posMappedName?: string
  // Direct children (recursive). Kept under the historical `subcategories`
  // name so the POS / Mapping tabs and the design mock share vocabulary.
  subcategories: WebCategory[]
  posCategories: LinkedPosCategory[]
}

export interface POSSubCategory {
  id: string
  name: string
  sync: string
  count: number
}

export interface POSCategory {
  id: string
  name: string
  count: number
  sync: string
  subcategories: POSSubCategory[]
  // Greenline company id (stringified). Only set for Greenline; subcategories
  // share their parent's company. Drives the per-company grouping in the POS
  // and Mapping tabs when a store links more than one company.
  companyId?: string
}

export interface Mapping {
  posId: string
  webId: string
}

export interface PantryCategoryFormValues {
  displayName: string
  slug: string
  // When true, typing in Display Name does NOT auto-derive the slug. Persisted
  // through the save path into `item_data.slug_locked` on the backend so the
  // lock affordance survives reload. Defaults to `false` (auto-derive).
  slugLocked: boolean
  visible: boolean
  headline: string
  description: string
  seoTitle: string
  seoDescription: string
  parentId?: string | number
  image?: unknown
  bannerImage?: unknown
}

export interface LinkedWebCategoryRef {
  /** Web category (or subcategory) id */
  id: string
  /** Display label; descendants use the full `Root › Parent › Child` path */
  label: string
  /** 'category' for root, 'subcategory' for any descendant row */
  kind: 'category' | 'subcategory'
  parentId?: string
  /** 0 = top-level, 1 = subcategory, 2 = sub-subcategory */
  depth?: number
}

export type LinkedPosCategoriesMap = Record<string, LinkedWebCategoryRef[]>

export interface LinkedPosCategoryRef {
  /** POS category (or subcategory) id */
  id: string
  /** Display label, typically the POS category name */
  label: string
}

/**
 * Inverse projection of CategoryMappingDict: webId → POS refs linked to it.
 * Built in CategoryContext by joining the unified mapping dict against the
 * POS-tree, so the Web tab can render badges without re-deriving from the
 * (now stale) embedded cova_category / greenline_category arrays.
 */
export type PosMappingsByWebId = Record<string, LinkedPosCategoryRef[]>

// Wire-level shape of the unified POS↔Web category-mapping endpoint:
//   GET /pantry/stores/:storeId/category-mapping
//   PUT /pantry/stores/:storeId/category-mapping
// posId → [webId, webId, ...]  (all IDs as strings inside the UI to match
// MappingTab's `idStr()` normalisation; the gateway coerces numeric webIds
// back to numbers on the way out for the PUT body).
export type CategoryMappingDict = Record<string, string[]>

// Bulk action selected from the Web tab's multi-select bar. `show`/`hide` set
// `visible` true/false; `delete` removes the selected categories.
export type BulkAction = 'show' | 'hide' | 'delete'
